import { hasMeetScopes } from "@kwp/contracts";
import type { AppRouter } from "@kwp/api/router";
import type { inferRouterOutputs } from "@trpc/server";
import { domainError } from "../../lib/api";

/** Pure helpers for the Meet button, the schedule form and the dashboard's meetings. */
type Outputs = inferRouterOutputs<AppRouter>;
export type Account = Outputs["accounts"]["list"][number];
export type Meeting = Outputs["meet"]["list"][number];

/** Google accounts can launch Meet; Outlook accounts cannot. */
export function googleAccounts(accounts: readonly Account[]): Account[] {
  return accounts.filter((a) => a.provider === "gmail");
}

export function meetEnabled(account: Pick<Account, "scopes">): boolean {
  return hasMeetScopes(account.scopes);
}

/** Who a meeting from this conversation can invite: the DM's people, or the workspace, not me. */
export function meetInvitees(
  channel: { kind: "channel" | "dm"; memberUids: readonly string[] },
  members: ReadonlyArray<{ uid: string; email: string | null }>,
  myUid: string,
): string[] {
  const inConversation = channel.kind === "dm" ? members.filter((m) => channel.memberUids.includes(m.uid)) : members;
  return inConversation.filter((m) => m.uid !== myUid).flatMap((m) => (m.email === null ? [] : [m.email.toLowerCase()]));
}

const EMAIL =/^[^\s@,;<>]+@[^\s@,;<>]+\.[^\s@,;<>]+$/;

/** Addresses from free text (commas, semicolons, spaces or new lines), lower-cased, once each. */
export function parseAttendees(text: string): { valid: string[]; invalid: string[] } {
  const parts = text
    .split(/[\s,;]+/)
    .map((p) => p.trim().toLowerCase())
    .filter((p) => p.length > 0);
  const valid = [...new Set(parts.filter((p) => EMAIL.test(p)))];
  const invalid = [...new Set(parts.filter((p) => !EMAIL.test(p)))];
  return { valid, invalid };
}

/** Start and end from the form's local date (YYYY-MM-DD), time (HH:MM) and minutes, or null. */
export function scheduleWindow(date: string, time: string, minutes: number): { start: Date; end: Date } | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}$/.test(time) || !Number.isFinite(minutes) || minutes <= 0) return null;
  const start = new Date(`${date}T${time}:00`);
  if (Number.isNaN(start.getTime())) return null;
  return { start, end: new Date(start.getTime() + minutes * 60_000) };
}

/** The form's defaults: the next half hour, today, in local time. */
export function nextSlot(now: Date): { date: string; time: string } {
  const slot = new Date(now.getTime());
  slot.setSeconds(0, 0);
  slot.setMinutes(slot.getMinutes() < 30 ? 30 : 60);
  const pad = (n: number) => String(n).padStart(2, "0");
  return { date: `${slot.getFullYear()}-${pad(slot.getMonth() + 1)}-${pad(slot.getDate())}`, time: `${pad(slot.getHours())}:${pad(slot.getMinutes())}` };
}

/** Local midnight to the next midnight around `now`: "today" for the dashboard. */
export function dayWindow(now: Date): { from: Date; to: Date } {
  const from = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const to = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  return { from, to };
}

const MEET_PARAMS = ["meet", "meetError", "account"];

/** Where consent should return to: this page, without the previous consent's result params. */
export function returnPath(location: { pathname: string; search: string }): string {
  const params = new URLSearchParams(location.search);
  for (const p of MEET_PARAMS) params.delete(p);
  const query = params.toString();
  return query.length === 0 ? location.pathname : `${location.pathname}?${query}`;
}

/** The consent result the api appended to the return URL, if any. */
export function consentResult(search: string): { kind: "enabled" } | { kind: "error"; reason: string } | null {
  const params = new URLSearchParams(search);
  if (params.get("meet") === "enabled") return { kind: "enabled" };
  const reason = params.get("meetError");
  return reason === null ? null : { kind: "error", reason };
}

const REASONS: Record<string, string> = {
  meet_scopes_missing: "Meet is not allowed for this Google account yet.",
  meet_wrong_account: "Google signed in with a different account. Allow Meet again and pick the same address.",
  not_google_account: "Meet needs a Google account; this one is not.",
  no_refresh_token: "Google did not return a token. Disconnect and reconnect the account, then try again.",
  meet_link_pending: "Google created the event but the Meet link is not ready yet. Check your calendar in a minute.",
  meet_link_failure: "Google could not add a Meet link to the event.",
  google_meet_unavailable: "Google Meet is not answering. Try again in a moment.",
  account: "That account is not yours or no longer exists.",
  channel: "That channel does not exist or you cannot post there.",
};

/** A sentence for a failed Meet call or consent, by domain reason. */
export function meetErrorText(errorOrReason: unknown): string {
  const reason = typeof errorOrReason === "string" ? errorOrReason : domainError(errorOrReason)?.reason;
  const known = reason === undefined ? undefined : REASONS[reason];
  if (known !== undefined) return known;
  const domain = typeof errorOrReason === "string" ? null : domainError(errorOrReason);
  if (domain?.code === "needs_reconnect") return "This account needs reconnecting in Settings → Accounts.";
  if (errorOrReason instanceof Error) return errorOrReason.message;
  return "Something went wrong with Meet.";
}

export function isScopesMissing(error: unknown): boolean {
  return domainError(error)?.reason === "meet_scopes_missing";
}

export function formatMeetingTime(m: Pick<Meeting, "startsAt" | "endsAt" | "kind">): string {
  const t = (d: Date) => d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  if (m.kind === "instant") return `Started ${t(m.startsAt)}`;
  return m.endsAt === null ? t(m.startsAt) : `${t(m.startsAt)}–${t(m.endsAt)}`;
}
